import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { TableRow as MuiTableRow } from '@mui/material'
import { useState, type RefObject, useEffect } from 'react'
import { PinnedCell } from 'shared/ui/TableNew/ui/PinnedCell/PinnedCell'
import { ColumnCell, DefaultRenderRowCell } from 'shared/ui/TableNew/ui/TableRow/ui/ColumnCell'
import { ActionsRow } from 'shared/ui/TableNew/ui/TableRow/ui/ActionsRow'
import { type IColumn, type IRowDefault, type ITableProps } from 'shared/ui/TableNew'
import { getAriaLabel } from 'shared/lib'
import { type IRow } from 'shared/ui/TableNew/store/types'
import commonStyles from 'shared/ui/TableNew/ui/Table.module.scss'
import styles from './styles.module.scss'
import { CheckboxCell } from '../CheckboxCell/CheckboxCell'

export type ITableRowProps<TRow extends IRowDefault> = {
  init?: () => (() => void) | void
  pinnedColumns: IColumn<TRow>[]
  unpinnedColumns: IColumn<TRow>[]
  withCheckbox?: ITableProps<TRow>['withCheckbox']
  row: IRow<TRow>
  selected?: boolean
  isScrollLeft?: boolean
  inAction?: boolean
  isHoveredRowActions?: boolean
  isCursor?: boolean
  onRowSelect?: (row: TRow, isSelect: boolean, isShiftKey: boolean) => void
  onRowClick?: ITableProps<TRow>['onRowClick']
  getRowLink?: ITableProps<TRow>['getRowLink']
  activeRowId?: ITableProps<TRow>['activeRowId']
  isCheckDate?: ITableProps<TRow>['isCheckDate']
  defaultWidthColumn?: ITableProps<TRow>['defaultWidthColumn']
  defaultMinWidthColumn?: ITableProps<TRow>['defaultMinWidthColumn']
  defaultMaxWidthColumn?: ITableProps<TRow>['defaultMaxWidthColumn']
  defaultColumnCellSx?: ITableProps<TRow>['defaultColumnCellSx']
  refTable: RefObject<HTMLDivElement>
}

export const TableRow = observer(
  <TRow extends IRowDefault>({
    pinnedColumns,
    unpinnedColumns,
    withCheckbox,
    row,
    inAction,
    onRowSelect,
    onRowClick,
    activeRowId,
    isCheckDate,
    getRowLink,
    defaultWidthColumn,
    defaultMinWidthColumn,
    defaultMaxWidthColumn,
    isCursor,
    isHoveredRowActions,
    refTable,
    defaultColumnCellSx,
    ...props
  }: ITableRowProps<TRow>) => {
    const [isHover, setIsHover] = useState(false)
    const [isRightOptionsOpen, setIsRightOptionsOpen] = useState(false)
    const isScrollLeft = props.isScrollLeft || false
    const selected = props.selected || false

    useEffect(() => {
      if (row.init) {
        return row.init()
      }
    }, [])

    const columnCellRender = (column: IColumn<TRow>, colSpan?: number) => {
      return (
        <ColumnCell
          key={column.field}
          row={row}
          column={column}
          getRowLink={getRowLink}
          onRowClick={onRowClick}
          defaultMinWidthColumn={defaultMinWidthColumn}
          defaultMaxWidthColumn={defaultMaxWidthColumn}
          defaultWidthColumn={defaultWidthColumn}
          defaultColumnCellSx={defaultColumnCellSx}
          colSpan={colSpan}
          isCursor={isCursor}
        >
          {(ref) => (
            <DefaultRenderRowCell
              row={row}
              column={column}
              hideExpandColumnsIds={[]}
              isHover={isHover}
              isCheckDate={isCheckDate}
              refTable={refTable}
              refCell={ref}
            />
          )}
        </ColumnCell>
      )
    }
    const activeRow = row.id === activeRowId
    const toggleSelected = (isSelected: boolean, isShiftKey: boolean) => {
      onRowSelect?.(row, isSelected, isShiftKey)
    }

    return (
      <MuiTableRow
        key={row.id}
        aria-label={row.ariaLabel ? getAriaLabel(row.ariaLabel) : undefined}
        className={classNames(styles.row, commonStyles.tableRow, {
          [commonStyles.activeRow]: activeRow,
          [commonStyles.selectedRow]: selected,
          [commonStyles.inActionRow]: inAction,
          [commonStyles.tableRowHovered]: isRightOptionsOpen || isHover,
        })}
        onMouseEnter={() => {
          setIsHover(true)
        }}
        onMouseLeave={() => {
          setIsHover(false)
        }}
      >
        {Boolean(pinnedColumns.length || withCheckbox) && (
          <PinnedCell
            isScrollLeft={isScrollLeft}
            width={!pinnedColumns.length ? 40 : undefined}
            withEmptyCell={pinnedColumns.length === 1 && !withCheckbox}
          >
            {withCheckbox && (
              <CheckboxCell
                disabled={
                  row.isDisabled ||
                  row.isCheckboxDisabled ||
                  row.origin?.isDisabled ||
                  row.origin?.isCheckboxDisabled
                }
                isHeader={true}
                selected={selected}
                onChangeValue={toggleSelected}
              />
            )}
            {pinnedColumns.map((column) => columnCellRender(column))}
          </PinnedCell>
        )}
        {unpinnedColumns.map((column, index, self) =>
          columnCellRender(column, index === self.length - 1 && !row.withActions ? 2 : undefined)
        )}

        {row.withActions && (
          <ActionsRow
            row={row}
            isHoveredRowActions={isHoveredRowActions}
            isRightOptionsOpen={isRightOptionsOpen}
            setIsRightOptionsOpen={setIsRightOptionsOpen}
          />
        )}
      </MuiTableRow>
    )
  }
)
